import { OrderResponse } from '@ca/common'
import CheckboxGroup from '@ui/forms/CheckboxGroup/CheckboxGroup'
import { OrderState } from '@order/hooks/state/orderState'
import { DiscountOption } from '@order/orderTypes'
import { groupBy } from 'lodash'
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const OrderSelect = ({
  items,
  order,
  discounts,
  active,
  setActive,
  validOrders,
  disabled,
}: {
  active: string[]
  setActive: Dispatch<SetStateAction<string[]>>
  discounts: DiscountOption[]
  items: OrderResponse[]
  order: OrderState
  validOrders?: number[]
  disabled?: boolean
}) => {
  const { t } = useTranslation(['summary'])

  const discountedItems = useMemo(() => {
    return (
      discounts?.flatMap((discount) =>
        discount.input.orders.map((order) => order.id?.toString())
      ) || []
    )
  }, [discounts])

  useEffect(() => {
    const discountedSet = new Set(discountedItems)
    const updated = active.filter((id) => !discountedSet.has(id))
    if (updated.length !== active.length) {
      setActive(updated)
    }
  }, [discountedItems, active, setActive])

  const options = useMemo(() => {
    const frameCounts = {
      base: 0,
      extra: 0,
    }
    const framesByType = groupBy(order.items, 'bundleType')

    return order.items
      .map((orderItem) => {
        const totalCount = framesByType[orderItem.bundleType]?.length || 1
        const item = items.find((item) => item.data.localId === orderItem.id)
        if (!item) return null

        frameCounts[orderItem.bundleType]++

        return {
          id: item.id,
          title: t(
            `summary:label.glasses.${orderItem.bundleType}.${
              totalCount === 1 ? 'single' : 'multiple'
            }`,
            {
              number: frameCounts[orderItem.bundleType],
            }
          ),
          disabled:
            disabled ||
            discountedItems.includes(item.id.toString()) ||
            (validOrders && !validOrders.includes(item.id)),
          image: orderItem.frame?.url ?? '',
          desc: item.name,
        }
      })
      .filter((v) => !!v)
  }, [items, order, t, discountedItems, validOrders, disabled])

  return (
    <CheckboxGroup active={active} setActive={setActive}>
      {options.map((item) => (
        <CheckboxGroup.ItemImage key={item.id} {...item} />
      ))}
    </CheckboxGroup>
  )
}

export default OrderSelect
